import React, { useState, useEffect } from 'react';
import { Search, MessageCircle, Trash2, ArrowRight, RefreshCw } from 'lucide-react';
import axios from 'axios';

interface StoryReply {
    id: number;
    story_id: number;
    story_owner_username: string;
    story_owner_name?: string;
    user_id: number;
    user_username: string;
    user_name?: string;
    user_avatar?: string;
    reply_text: string;
    created_at: string;
}

interface ReplyStats {
    total_replies: number;
    recent_replies: number;
    active_repliers: number;
}

const StoryReplyManagement: React.FC = () => {
    const [replies, setReplies] = useState<StoryReply[]>([]);
    const [stats, setStats] = useState<ReplyStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setRefreshing(true);
        try {
            const [repliesRes, statsRes] = await Promise.all([
                axios.get('https://api.lifeshare.app/api/v1/admin/story-replies'),
                axios.get('https://api.lifeshare.app/api/v1/admin/story-replies/stats')
            ]);
            setReplies(repliesRes.data.replies || []);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleDelete = async (replyId: number) => {
        if (window.confirm('Bu yanıtı silmek istediğinizden emin misiniz?')) {
            try {
                await axios.delete(`https://api.lifeshare.app/api/v1/admin/story-replies/${replyId}`);
                setReplies(replies.filter(r => r.id !== replyId));
            } catch (error: any) {
                alert('Silme başarısız: ' + (error.response?.data?.detail || 'Hata'));
            }
        }
    };

    const filteredReplies = replies.filter(r =>
        r.user_username.toLowerCase().includes(searchText.toLowerCase()) ||
        r.story_owner_username.toLowerCase().includes(searchText.toLowerCase()) ||
        r.reply_text.toLowerCase().includes(searchText.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="max-w-5xl mx-auto space-y-6">

                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Hikaye Yanıtları</h1>
                        <p className="text-sm text-gray-500 mt-1">Hikayelere yapılan yanıtları yönet</p>
                    </div>
                    <button onClick={fetchData} disabled={refreshing} className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors">
                        <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-3 gap-3">
                        <div className="bg-gradient-to-br from-pink-50 to-pink-100 border border-pink-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><MessageCircle className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-pink-700">{stats.total_replies}</p>
                            <p className="text-xs text-pink-600">Toplam</p>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-4">
                            <p className="text-2xl font-bold text-blue-700">{stats.recent_replies}</p>
                            <p className="text-xs text-blue-600">Son 7 Gün</p>
                        </div>
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-4">
                            <p className="text-2xl font-bold text-purple-700">{stats.active_repliers}</p>
                            <p className="text-xs text-purple-600">Aktif Yanıtlayan</p>
                        </div>
                    </div>
                )}

                {/* Search */}
                <div className="bg-white rounded-2xl p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type="text" placeholder="Kullanıcı veya metin ara..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500" />
                    </div>
                </div>

                {/* List */}
                {filteredReplies.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center">
                        <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">Hikaye yanıtı bulunamadı</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filteredReplies.map((reply) => (
                            <div key={reply.id} className="bg-white rounded-2xl p-4 hover:ring-2 hover:ring-purple-100 transition-all">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                        {reply.user_avatar ? (
                                            <img src={`https://api.lifeshare.app${reply.user_avatar}`} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                                        ) : (
                                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-medium flex-shrink-0">
                                                {(reply.user_name || reply.user_username)[0].toUpperCase()}
                                            </div>
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="font-medium text-gray-900">{reply.user_name || reply.user_username}</span>
                                                <ArrowRight className="w-3 h-3 text-gray-400" />
                                                <span className="text-gray-600">{reply.story_owner_name || reply.story_owner_username}'s story</span>
                                            </div>
                                            <p className="mt-2 text-sm text-gray-700 bg-gray-50 p-3 rounded-xl">"{reply.reply_text}"</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-400">{new Date(reply.created_at).toLocaleDateString('tr-TR')}</span>
                                        <button onClick={() => handleDelete(reply.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-xl">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default StoryReplyManagement;
